import { inject, Injectable } from '@angular/core';
import { FirebaseApp, getApp, initializeApp } from '@angular/fire/app';
import {
  Auth,
  getAuth,
  signInWithCustomToken,
} from '@angular/fire/auth';
import {
  Firestore,
  getFirestore,
  initializeFirestore,
} from '@angular/fire/firestore';
import {
  Functions,
  getFunctions,
  httpsCallable,
} from '@angular/fire/functions';
import { ENVIRONMENT } from '@models';

const MASTER_APP_NAME = 'support-master';
const FUNCTIONS_REGION = 'europe-west1';

/**
 * Gives the support & hours module a connection to the MASTER project, where
 * that data is the single source of truth.
 *
 * - role 'master' (SaaS-resident deployments): support data already lives in the
 *   primary project → `db`/`functions` are the primary instances. No-op.
 * - role 'client' (standalone deployments on their own Firebase): obtains a
 *   scoped support-session custom token (via the local `getSupportSession`
 *   callable → master's hub), signs into a secondary Firebase app pointed at the
 *   master project, and exposes that app's Firestore/Functions. The existing
 *   support UI then reads/writes master directly under master's rules.
 *
 * Consumers must `await ensure()` before using `db` / `functions`.
 */
@Injectable({ providedIn: 'root' })
export class SupportSessionService {
  private readonly env = inject<any>(ENVIRONMENT);
  private readonly primaryDb = inject(Firestore);
  private readonly primaryFunctions = inject(Functions);

  private _db?: Firestore;
  private _functions?: Functions;
  private establishing?: Promise<void>;

  /** 'master' when support data lives in this deployment; 'client' when remote. */
  get role(): 'master' | 'client' {
    return this.env?.supportHub?.role === 'client' ? 'client' : 'master';
  }

  /** Active Firestore for support/hours — master project when role is 'client'. */
  get db(): Firestore {
    return this._db ?? this.primaryDb;
  }

  /** Active Functions for support/hours — master project when role is 'client'. */
  get functions(): Functions {
    return this._functions ?? this.primaryFunctions;
  }

  /**
   * Ensures `db`/`functions` point at the master project. Safe to call repeatedly;
   * the client-side sign-in happens once and is cached.
   */
  async ensure(): Promise<void> {
    if (this.role === 'master') {
      this._db = this.primaryDb;
      this._functions = this.primaryFunctions;
      return;
    }
    if (!this.establishing) {
      this.establishing = this.establishClientSession().catch((err) => {
        // Allow a later retry if the session could not be established.
        this.establishing = undefined;
        throw err;
      });
    }
    return this.establishing;
  }

  private async establishClientSession(): Promise<void> {
    const masterConfig = this.env?.supportHub?.master;
    if (!masterConfig) {
      throw new Error(
        'supportHub.master (master Firebase config) no está configurado en el environment',
      );
    }

    // 1. Ask this deployment's callable for a scoped master session token.
    const getSession = httpsCallable<unknown, { token: string }>(
      this.primaryFunctions,
      'getSupportSession',
    );
    const { data } = await getSession({});
    if (!data?.token) {
      throw new Error('getSupportSession no devolvió un token');
    }

    // 2. Sign into a secondary Firebase app pointed at the master project.
    let app: FirebaseApp;
    try {
      app = getApp(MASTER_APP_NAME);
    } catch {
      app = initializeApp(masterConfig, MASTER_APP_NAME);
    }

    const masterAuth: Auth = getAuth(app);
    await signInWithCustomToken(masterAuth, data.token);

    // 3. Expose the master app's Firestore + Functions.
    try {
      this._db = getFirestore(app);
    } catch {
      this._db = initializeFirestore(app, { ignoreUndefinedProperties: true });
    }
    this._functions = getFunctions(app, FUNCTIONS_REGION);
  }
}
